import { ArrowRight, ChevronUp, Menu, X, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SharedFooter } from "@/components/common/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";
import { useStartEvaluation } from "@/hooks/useStartEvaluation";

const functions = [
  {
    code: "GV",
    name: "Guvernare",
    color: "#E8872A",
    borderColor: "#E8872A4D",
    reqs: "40 cerinte, 12 MC",
    desc: "Stratul persistent — fundatia intregului program de securitate cibernetica.",
  },
  {
    code: "ID",
    name: "Identificare",
    color: "#60A5FA",
    borderColor: "#60A5FA4D",
    reqs: "55 cerinte, 6 MC",
    desc: "Ce avem, ce riscuri purtam, unde sunt punctele critice.",
  },
  {
    code: "PR",
    name: "Protectie",
    color: "#22C55E",
    borderColor: "#22C55E4D",
    reqs: "79 cerinte, MC multiple",
    desc: "Controale tehnice si organizationale — cel mai mare volum de implementare.",
  },
  {
    code: "DE",
    name: "Detectare",
    color: "#A78BFA",
    borderColor: "#A78BFA4D",
    reqs: "20 cerinte, 4 MC",
    desc: "Monitorizare continua, analiza evenimente adverse.",
  },
  {
    code: "RS",
    name: "Raspuns",
    color: "#F87171",
    borderColor: "#F871714D",
    reqs: "16 cerinte, 3 MC",
    desc: "Gestionare incidente, raportare PNRISC.",
  },
  {
    code: "RC",
    name: "Recuperare",
    color: "#FBBF24",
    borderColor: "#FBBF244D",
    reqs: "8 cerinte, 1 MC",
    desc: "Continuitate, redresare, lectii invatate.",
  },
];

const gvCategories = [
  {
    code: "GV.OC",
    name: "Context Organizational",
    count: "9 cerinte",
    mc: null,
  },
  {
    code: "GV.RM",
    name: "Strategia Management Riscuri",
    count: "7 cerinte",
    mc: "MC: GV.RM-03.1",
  },
  {
    code: "GV.RR",
    name: "Roluri Responsabilitati",
    count: "5 cerinte",
    mc: null,
  },
  { code: "GV.PO", name: "Politici", count: "4 cerinte", mc: null },
  {
    code: "GV.OV",
    name: "Supraveghere",
    count: "5 cerinte",
    mc: "MC: GV.OV-03.1 KPI",
  },
  {
    code: "GV.SC",
    name: "Securitate Lant Aprovizionare",
    count: "10 cerinte",
    mc: "MC: GV.SC-03.1 doar Esential",
  },
];

const idCategories = [
  {
    code: "ID.AM",
    name: "Gestionare Active",
    count: "28 cerinte",
    mc: "MC: ID.AM-08.2 patch",
  },
  {
    code: "ID.RA",
    name: "Evaluare Riscuri",
    count: "16 cerinte",
    mc: "MC: ID.RA-05.2/3, ID.RA-08.1",
  },
  {
    code: "ID.IM",
    name: "Imbunatatire",
    count: "11 cerinte",
    mc: "MC: ID.IM-03.3/9, ID.IM-04.1/2",
  },
];

const prCategories = [
  {
    code: "PR.AA",
    name: "Autentificare Autorizare",
    count: "24 cerinte",
    mc: "TOATE MC",
  },
  {
    code: "PR.AT",
    name: "Constientizare Formare",
    count: "cerinte multiple",
    mc: null,
  },
  {
    code: "PR.DS",
    name: "Securitatea Datelor",
    count: "cerinte multiple",
    mc: "MC: PR.DS-11.1 backup",
  },
  {
    code: "PR.PS",
    name: "Securitate Platforme",
    count: "cerinte multiple",
    mc: "MC: PR.PS-04.1 logging",
  },
  {
    code: "PR.IR",
    name: "Rezilienta Infrastructurii",
    count: "cerinte multiple",
    mc: "MC: PR.IR-01.1 FW + MC: PR.IR-01.2 segmentare",
  },
];

const deCategories = [
  {
    code: "DE.CM",
    name: "Monitorizare Continua",
    desc: "Antimalware 100%, SIEM/SOAR >= 20 reguli, SOC 24/7 Esential.",
    mc: "MC: DE.CM-01.2",
  },
  {
    code: "DE.AE",
    name: "Analiza Evenimente",
    desc: "Logging centralizat >= 90 zile, corelatie.",
    mc: "MC: DE.AE-03.1",
  },
];

const rsCategories = [
  { code: "RS.MA", name: "Management Incidente", desc: null, mc: null },
  { code: "RS.AN", name: "Analiza Incidente", desc: null, mc: null },
  {
    code: "RS.CO",
    name: "Raportare Comunicare",
    desc: "PNRISC art.15.",
    mc: "MC: RS.CO-02.2",
  },
  {
    code: "RS.MI",
    name: "Atenuarea Incidentelor",
    desc: null,
    mc: "MC: RS.MI-01.2",
  },
];

const rcCategories = [
  { code: "RC.RP", name: "Executie Plan Recuperare", desc: null, mc: null },
  {
    code: "RC.CO",
    name: "Comunicare Recuperare",
    desc: null,
    mc: "MC: RC.CO-03.1",
  },
];

const maturityLevels = [
  {
    level: 1,
    color: "#F87171",
    name: "Initial",
    desc: "Fara documentatie formala. Procese ad-hoc, reactive, nerepetabile.",
    highlighted: false,
  },
  {
    level: 2,
    color: "#FBBF24",
    name: "Repetabil",
    desc: "Politici aprobate dar incomplete. Implementare partiala, fara metrici.",
    highlighted: false,
  },
  {
    level: 3,
    color: "#22C55E",
    name: "Definit",
    desc: "Aprobat formal, implementat cu dovezi. Pragul minim NIS2 — Nivel 3 pe toate MC.",
    highlighted: true,
  },
  {
    level: 4,
    color: "#60A5FA",
    name: "Gestionat",
    desc: "Cu metrici KPI/KRI. Monitorizare cantitativa a eficacitatii controalelor.",
    highlighted: false,
  },
  {
    level: 5,
    color: "#A78BFA",
    name: "Optimizat",
    desc: "Imbunatatire continua bazata pe lectii invatate, benchmarking si feedback ciclic.",
    highlighted: false,
  },
];

const nis2Mapping = [
  {
    art: "Art.13(a)",
    obligation: "Analiza de risc si politici",
    cyfun: "GV.RM, ID.RA",
  },
  {
    art: "Art.13(b)",
    obligation: "Evaluare eficacitate KPI",
    cyfun: "GV.OV, DE.CM",
  },
  {
    art: "Art.13(c)",
    obligation: "Criptografie protectia datelor",
    cyfun: "PR.DS",
  },
  {
    art: "Art.13(d)",
    obligation: "Securitate lant aprovizionare",
    cyfun: "GV.SC",
  },
  {
    art: "Art.13(e)",
    obligation: "Securitate achizitie dezvoltare",
    cyfun: "ID.AM, PR.PS",
  },
  {
    art: "Art.13(f)",
    obligation: "Inventar active Control acces MFA",
    cyfun: "ID.AM, PR.AA",
  },
  {
    art: "Art.13(g)",
    obligation: "Gestionarea incidentelor",
    cyfun: "RS.MA, RS.CO",
  },
  {
    art: "Art.13(h)",
    obligation: "Continuitate Backup Redresare",
    cyfun: "PR.DS, RC.RP",
  },
  { art: "Art.13(i)", obligation: "Formare personal", cyfun: "PR.AT" },
  {
    art: "Art.13(j)",
    obligation: "Securitate retea Firewall",
    cyfun: "PR.AA, PR.IR",
  },
  { art: "Art.15", obligation: "Raportare incidente PNRISC", cyfun: "RS.CO" },
  {
    art: "Art.14(1-2)",
    obligation: "Aprobare conducere Formare management",
    cyfun: "GV.RR, PR.AT",
  },
];

const CyFunFundamentals = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isLoggedIn = !!user;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollRafId = useRef(0);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (scrollRafId.current) return;
      scrollRafId.current = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 600);
        scrollRafId.current = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafId.current) cancelAnimationFrame(scrollRafId.current);
    };
  }, []);

  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const setSectionRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      sectionRefs.current[index] = el;
    },
    []
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("cf-section-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );
    for (const el of sectionRefs.current) {
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  const goToAuth = () => navigate("/auth");
  const goToSignin = () => navigate("/auth?mode=signin");
  const goToDashboard = () => navigate("/companiile-mele");
  const goToLogout = () => navigate("/logout");
  const goToLanding = () => navigate("/");
  const startEvaluation = useStartEvaluation();

  return (
    <div className="cf">
      <style>{`
        .cf * { box-sizing: border-box; margin: 0; padding: 0; }
        .cf { background: #FFFFFF; color: #0F172A; font-family: 'Roboto Mono', monospace; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        .cf a, .cf button { font-family: inherit; }
        .cf button:focus-visible, .cf a:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }

        /* ===== SKIP LINK ===== */
        .cf-skip { position: absolute; left: -9999px; top: 0; z-index: 999; padding: 12px 24px; background: #2563EB; color: #FFF; font-size: 14px; border-radius: 0 0 8px 0; text-decoration: none; }
        .cf-skip:focus { left: 0; }

        /* ===== LAYOUT ===== */
        .cf-inner { max-width: 1120px; margin: 0 auto; padding: 0 32px; }
        .cf-sora { font-family: 'Sora', sans-serif; }

        /* ===== TYPOGRAPHY ===== */
        .cf-eyebrow { font-size: 12px; letter-spacing: 1.4px; color: #2563EB; font-weight: 700; font-family: 'Roboto Mono', monospace; text-transform: uppercase; }
        .cf-h2 { font-family: 'Sora', sans-serif; font-size: clamp(32px, 4vw, 42px); font-weight: 700; letter-spacing: -1.3px; line-height: 1.15; color: #0F172A; }
        .cf-h3 { font-family: 'Sora', sans-serif; font-size: clamp(30px, 3.5vw, 38px); font-weight: 700; line-height: 1.15; color: #0F172A; }
        .cf-body { font-size: 14px; line-height: 1.7; color: #475569; }

        /* ===== BUTTONS ===== */
        .cf-btn { border: none; border-radius: 999px; padding: 14px 28px; font-weight: 500; font-size: 14px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 150ms ease; text-decoration: none; font-family: 'Roboto Mono', monospace; }
        .cf-btn-primary { background: #FF8400; color: #111; }
        .cf-btn-primary:hover { background: #FF9520; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }
        .cf-btn-ghost { background: transparent; color: #FFF; border: 1px solid #60A5FA; }
        .cf-btn-ghost:hover { border-color: #93C5FD; color: #BFDBFE; }

        /* ===== HERO ===== */
        .cf-hero { background: #0F172A; padding: 28px 0 80px; display: flex; flex-direction: column; }
        .cf-hero > * { max-width: 1120px; margin-left: auto; margin-right: auto; padding-left: 32px; padding-right: 32px; width: 100%; }

        /* ===== NAV ===== */
        .cf-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 40px; }
        .cf-logo { display: flex; align-items: center; gap: 8px; cursor: pointer; border-radius: 6px; padding: 4px 8px; margin: -4px -8px; }
        .cf-logo:focus-visible { outline: 2px solid #60A5FA; outline-offset: 2px; }
        .cf-logo-mark { width: 14px; height: 14px; background: #22C55E; border-radius: 2px; flex-shrink: 0; }
        .cf-logo-text { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #FFF; }
        .cf-nav-links { display: flex; align-items: center; gap: 24px; }
        .cf-nav-link { font-size: 12px; color: #CBD5E1; cursor: pointer; transition: color 150ms; background: none; border: none; padding: 0; }
        .cf-nav-link:hover { color: #FFF; }
        .cf-nav-link.active { color: #60A5FA; }
        .cf-nav-btns { display: flex; gap: 12px; align-items: center; }
        .cf-nav-ghost { font-size: 12px; background: transparent; border: 1px solid #334155; color: #CBD5E1; padding: 10px 18px; border-radius: 999px; cursor: pointer; transition: all 200ms ease; }
        .cf-nav-ghost:hover { border-color: #60A5FA; color: #FFF; background: rgba(96,165,250,0.08); }
        .cf-nav-cta { font-size: 12px; background: #FF8400; color: #111; border: none; padding: 10px 18px; border-radius: 999px; cursor: pointer; font-weight: 500; transition: all 200ms ease; }
        .cf-nav-cta:hover { background: #FF9520; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(255,132,0,0.3); }

        /* ===== HERO BODY ===== */
        .cf-hero-body { display: flex; flex-direction: column; align-items: center; gap: 20px; text-align: center; }
        .cf-hero-label { font-size: 12px; letter-spacing: 1.4px; color: #60A5FA; font-weight: 700; font-family: 'Roboto Mono', monospace; }
        .cf-hero-title { font-family: 'Sora', sans-serif; font-size: clamp(36px, 4.5vw, 48px); font-weight: 800; color: #FFF; letter-spacing: -1.8px; line-height: 1.06; max-width: 1000px; }
        .cf-hero-sub { font-size: 14px; line-height: 1.5; color: #C7D2FE; max-width: 900px; }

        /* ===== SECTION BACKGROUNDS ===== */
        .cf-section-light { background: #F8FAFC; padding: 80px 0; }
        .cf-section-white { background: #FFFFFF; padding: 80px 0; }
        .cf-section-dark { background: #0F172A; padding: 80px 0; }
        .cf-section-darker { background: #111827; padding: 80px 0; }

        /* ===== BADGE PILL ===== */
        .cf-pill { display: inline-block; font-size: 12px; color: #475569; background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 999px; padding: 12px 20px; font-family: 'Roboto Mono', monospace; }

        /* ===== CARDS ===== */
        .cf-card { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 16px; transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease; }
        .cf-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
        .cf-card-tag { font-size: 10px; font-weight: 700; letter-spacing: 1.2px; color: #1D4ED8; font-family: 'Roboto Mono', monospace; text-transform: uppercase; }
        .cf-card-title { font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 700; color: #0F172A; }
        .cf-card-body { font-size: 13px; color: #475569; line-height: 1.7; font-family: 'Roboto Mono', monospace; }
        .cf-card-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .cf-card-pill { font-size: 10px; font-weight: 500; color: #1D4ED8; background: #EFF6FF; border-radius: 999px; padding: 4px 10px; font-family: 'Roboto Mono', monospace; }
        .cf-intro-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 32px; }

        /* ===== 6 FUNCTIONS GRID ===== */
        .cf-fn-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-top: 32px; }
        .cf-fn-card { background: #FFFFFF; border-radius: 12px; padding: 24px; display: flex; flex-direction: column; gap: 8px; transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease; }
        .cf-fn-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.06); }
        .cf-fn-code { font-size: 14px; font-weight: 700; font-family: 'Roboto Mono', monospace; }
        .cf-fn-title { font-family: 'Sora', sans-serif; font-size: 18px; font-weight: 700; color: #0F172A; }
        .cf-fn-stats { font-size: 11px; color: #64748B; font-family: 'Roboto Mono', monospace; }
        .cf-fn-desc { font-size: 12px; color: #475569; font-family: 'Roboto Mono', monospace; line-height: 1.5; }

        /* ===== CHAIN VISUALIZATION ===== */
        .cf-chain { display: flex; align-items: center; justify-content: center; gap: 8px; flex-wrap: wrap; margin-top: 32px; }
        .cf-chain-pill { border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 600; font-family: 'Roboto Mono', monospace; }
        .cf-chain-arrow { font-size: 14px; color: #94A3B8; font-family: 'Roboto Mono', monospace; }
        .cf-chain-note { font-size: 12px; color: #64748B; text-align: center; margin-top: 16px; max-width: 800px; margin-left: auto; margin-right: auto; line-height: 1.5; }
        .cf-chain-summary { display: inline-block; font-size: 12px; font-weight: 700; color: #475569; background: #F1F5F9; border-radius: 999px; padding: 8px 20px; margin-top: 16px; font-family: 'Roboto Mono', monospace; }

        /* ===== FUNCTION DETAIL CARDS ===== */
        .cf-detail-card { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 20px; margin-top: 24px; }
        .cf-detail-card-alt { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px; padding: 28px; display: flex; flex-direction: column; gap: 20px; margin-top: 24px; }
        .cf-detail-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .cf-detail-badge { border-radius: 6px; padding: 6px 12px; font-size: 14px; font-weight: 700; font-family: 'Roboto Mono', monospace; }
        .cf-detail-badge-solid { border-radius: 6px; padding: 6px 12px; font-size: 14px; font-weight: 700; font-family: 'Roboto Mono', monospace; color: #FFF; }
        .cf-detail-title { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: #0F172A; }
        .cf-detail-desc { font-size: 13px; color: #475569; line-height: 1.7; }
        .cf-subcats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .cf-subcats-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .cf-subcat { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 8px; padding: 16px; display: flex; flex-direction: column; gap: 8px; }
        .cf-subcat-code { font-size: 12px; font-weight: 700; color: #0F172A; font-family: 'Roboto Mono', monospace; }
        .cf-subcat-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 600; color: #0F172A; }
        .cf-subcat-count { font-size: 11px; color: #64748B; font-family: 'Roboto Mono', monospace; }
        .cf-mc-tag { display: inline-block; font-size: 10px; font-weight: 500; color: #DC2626; background: #FEE2E2; border-radius: 4px; padding: 3px 8px; font-family: 'Roboto Mono', monospace; width: fit-content; }

        /* ===== DE/RS/RC CATEGORY LIST ===== */
        .cf-cat-list { display: flex; flex-direction: column; gap: 8px; }
        .cf-cat-item { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 8px; padding: 14px 16px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px; }
        .cf-cat-item-left { display: flex; flex-direction: column; gap: 2px; }
        .cf-cat-item-code { font-size: 12px; font-weight: 700; color: #0F172A; font-family: 'Roboto Mono', monospace; }
        .cf-cat-item-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 600; color: #0F172A; }
        .cf-cat-item-desc { font-size: 11px; color: #64748B; font-family: 'Roboto Mono', monospace; margin-top: 2px; line-height: 1.5; }

        /* ===== MATURITY LEVELS ===== */
        .cf-levels { display: flex; border-radius: 12px; background: #F8FAFC; border: 1px solid #E2E8F0; overflow: hidden; margin-top: 32px; }
        .cf-level { flex: 1; padding: 24px; display: flex; flex-direction: column; gap: 12px; border-right: 1px solid #E2E8F0; }
        .cf-level:last-child { border-right: none; }
        .cf-level-highlighted { border-top: 3px solid #22C55E; }
        .cf-level-num { font-family: 'Sora', sans-serif; font-size: 32px; font-weight: 700; }
        .cf-level-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 600; color: #0F172A; }
        .cf-level-desc { font-size: 12px; color: #475569; line-height: 1.5; font-family: 'Roboto Mono', monospace; }

        /* ===== CALLOUT ===== */
        .cf-callout { border-left: 3px solid #E8872A; background: rgba(232,135,42,0.05); border-radius: 8px; padding: 20px; margin-top: 24px; }
        .cf-callout-text { font-size: 13px; color: #475569; line-height: 1.6; }

        /* ===== TABLE ===== */
        .cf-table-wrap { border-radius: 12px; border: 1px solid #E2E8F0; overflow: hidden; margin-top: 24px; background: #FFFFFF; position: relative; }
        .cf-table-scroll-hint { display: none; font-size: 11px; color: #64748B; text-align: center; margin-top: 8px; font-family: 'Roboto Mono', monospace; }
        .cf-table { width: 100%; border-collapse: collapse; font-family: 'Roboto Mono', monospace; }
        .cf-table th { background: #F8FAFC; font-size: 12px; font-weight: 700; color: #0F172A; text-align: left; padding: 12px 16px; border-bottom: 1px solid #E2E8F0; }
        .cf-table td { font-size: 12px; color: #0F172A; padding: 12px 16px; border-bottom: 1px solid #E2E8F0; }
        .cf-table tr:last-child td { border-bottom: none; }
        .cf-table-art { color: #E8872A; font-weight: 700; white-space: nowrap; }
        .cf-table-cyfun { color: #1D4ED8; font-weight: 700; }

        /* ===== WARNING CALLOUT ===== */
        .cf-warning { background: #FFF7ED; border: 1px solid #FDBA74; border-radius: 10px; padding: 16px 20px; display: flex; align-items: flex-start; gap: 12px; margin-top: 24px; }
        .cf-warning-icon { color: #E8872A; flex-shrink: 0; margin-top: 2px; }
        .cf-warning-text { font-size: 13px; color: #9A3412; line-height: 1.6; }

        /* ===== EVALUATION STEPS ===== */
        .cf-steps { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin-top: 24px; }
        .cf-step { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 10px; padding: 24px; display: flex; flex-direction: column; gap: 12px; transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease; }
        .cf-step:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.06); }
        .cf-step-num { font-size: 11px; font-weight: 700; color: #E8872A; font-family: 'Roboto Mono', monospace; letter-spacing: 1px; }
        .cf-step-title { font-family: 'Sora', sans-serif; font-size: 17px; font-weight: 700; color: #0F172A; line-height: 1.3; }
        .cf-step-desc { font-size: 13px; color: #475569; line-height: 1.6; }

        /* ===== CTA SECTION ===== */
        .cf-cta-stats { display: flex; justify-content: center; gap: 48px; margin-top: 32px; flex-wrap: wrap; }
        .cf-cta-stat-val { font-family: 'Sora', sans-serif; font-size: clamp(28px, 3vw, 36px); font-weight: 800; }
        .cf-cta-stat-label { font-size: 11px; color: #94A3B8; font-family: 'Roboto Mono', monospace; margin-top: 4px; }
        .cf-cta-buttons { display: flex; gap: 14px; justify-content: center; margin-top: 32px; flex-wrap: wrap; }
        .cf-cta-note { font-size: 11px; color: #64748B; text-align: center; margin-top: 16px; }

        /* ===== MOBILE MENU ===== */
        .cf-menu-btn { display: none; border: 1px solid #334155; background: transparent; color: #E2E8F0; border-radius: 10px; width: 44px; height: 44px; align-items: center; justify-content: center; cursor: pointer; }
        .cf-mobile-menu { display: none; border: 1px solid #334155; background: #0B1220; border-radius: 14px; padding: 16px; margin-bottom: 24px; }
        .cf-mobile-cta { display: none; position: sticky; bottom: 0; z-index: 80; background: rgba(11,18,32,0.94); backdrop-filter: blur(10px); border-top: 1px solid #334155; padding: 10px 12px; }
        .cf-mobile-cta button { width: 100%; justify-content: center; }

        /* ===== BACK-TO-TOP BUTTON ===== */
        .cf-back-to-top { position: fixed; bottom: 96px; right: 28px; z-index: 90; width: 44px; height: 44px; border-radius: 50%; background: #2563EB; color: #FFF; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 16px rgba(37,99,235,0.35); opacity: 0; transform: translateY(16px) scale(0.85); transition: opacity 0.3s ease, transform 0.3s ease, background 0.15s ease; pointer-events: none; }
        .cf-back-to-top.visible { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
        .cf-back-to-top:hover { background: #1D4ED8; transform: translateY(-2px) scale(1.05); }
        .cf-back-to-top:focus-visible { outline: 2px solid #60A5FA; outline-offset: 3px; }

        /* ===== SECTION FADE-IN ===== */
        @keyframes cf-fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .cf-section-reveal { opacity: 0; transform: translateY(24px); }
        .cf-section-visible { animation: cf-fadeInUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards; }

        /* ===== SMOOTH SCROLL ===== */
        .cf { scroll-behavior: smooth; }

        /* ===== THRESHOLD TABLE ===== */
        .cf-threshold-header-green { background: rgba(34,197,94,0.1); color: #22C55E; }
        .cf-threshold-header-yellow { background: rgba(251,191,36,0.1); color: #B45309; }
        .cf-threshold-header-red { background: rgba(248,113,113,0.1); color: #F87171; }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 1080px) {
          .cf-intro-cards, .cf-fn-grid, .cf-subcats, .cf-subcats-3, .cf-steps { grid-template-columns: 1fr; }
          .cf-levels { flex-direction: column; }
          .cf-level { border-right: none; border-bottom: 1px solid #E2E8F0; }
          .cf-level:last-child { border-bottom: none; }
          .cf-cta-stats { gap: 24px; }
        }
        @media (max-width: 920px) {
          .cf-inner, .cf-hero > * { padding-left: 24px; padding-right: 24px; }
          .cf-nav-links, .cf-nav-btns { display: none; }
          .cf-menu-btn { display: inline-flex; }
          .cf-mobile-menu { display: block; }
          .cf-mobile-cta { display: flex; }
          .cf-hero-title { font-size: clamp(28px, 6vw, 36px); }
          .cf-fn-grid { grid-template-columns: 1fr; }
          .cf-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .cf-table { min-width: 600px; }
          .cf-chain { gap: 4px; }
          .cf-chain-pill { font-size: 11px; padding: 4px 10px; }
          .cf-body { font-size: 15px; line-height: 1.75; }
          .cf-detail-desc { font-size: 14px; line-height: 1.75; }
          .cf-subcat { padding: 20px; }
          .cf-detail-card, .cf-detail-card-alt { margin-top: 32px; }
          .cf-table-scroll-hint { display: block; }
          .cf-warning-text { font-size: 14px; }
        }
        @media (max-width: 640px) {
          .cf-hero { padding: 20px 0 48px; }
          .cf-section-light, .cf-section-white, .cf-section-dark, .cf-section-darker { padding: 48px 0; }
          .cf-detail-header { flex-direction: column; align-items: flex-start; }
          .cf-cta-stats { flex-direction: column; align-items: center; gap: 16px; }
          .cf-cta-buttons { flex-direction: column; align-items: stretch; }
          .cf-cta-buttons .cf-btn { justify-content: center; }
          .cf-hero-sub { font-size: 13px; line-height: 1.6; }
          .cf-fn-desc { font-size: 13px; line-height: 1.6; }
          .cf-cat-item { padding: 16px 20px; }
          .cf-cat-item-desc { font-size: 12px; }
          .cf-callout { padding: 16px; }
          .cf-callout-text { font-size: 13px; }
        }
      `}</style>

      <a href="#main-content" className="cf-skip">
        Sari la continut
      </a>

      {/* ====== SECTION 0: HERO ====== */}
      <header className="cf-hero" id="main-content">
        <nav className="cf-nav" aria-label="Navigare principala">
          <div
            className="cf-logo"
            onClick={goToLanding}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && goToLanding()}
            aria-label="NIS2PASS - Pagina principala"
          >
            <span className="cf-logo-mark" aria-hidden="true" />
            <span className="cf-logo-text">NIS2PASS</span>
          </div>
          <div className="cf-nav-links">
            <button type="button" className="cf-nav-link" onClick={goToLanding}>
              Platforma
            </button>
            <button type="button" className="cf-nav-link active">
              CyFun Framework
            </button>
            <button
              type="button"
              className="cf-nav-link"
              onClick={() => navigate("/termeni-si-conditii")}
            >
              Legal
            </button>
            <button
              type="button"
              className="cf-nav-link"
              onClick={() => navigate("/contact")}
            >
              Contact
            </button>
          </div>
          <div className="cf-nav-btns">
            {!loading &&
              (isLoggedIn ? (
                <>
                  <button
                    type="button"
                    className="cf-nav-cta"
                    onClick={goToDashboard}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className="cf-nav-ghost"
                    onClick={goToLogout}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="cf-nav-ghost"
                    onClick={goToSignin}
                  >
                    Intra in cont
                  </button>
                  <button
                    type="button"
                    className="cf-nav-cta"
                    onClick={goToAuth}
                  >
                    Creeaza cont gratuit
                  </button>
                </>
              ))}
          </div>
          <button
            type="button"
            className="cf-menu-btn"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? "Inchide meniu" : "Deschide meniu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </nav>

        {mobileMenuOpen && (
          <div
            className="cf-mobile-menu"
            role="navigation"
            aria-label="Meniu mobil"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <button
                type="button"
                className="cf-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  goToLanding();
                }}
              >
                Platforma
              </button>
              <button type="button" className="cf-nav-link active">
                CyFun Framework
              </button>
              <button
                type="button"
                className="cf-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/termeni-si-conditii");
                }}
              >
                Legal
              </button>
              <button
                type="button"
                className="cf-nav-link"
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/contact");
                }}
              >
                Contact
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {isLoggedIn ? (
                <>
                  <button
                    type="button"
                    className="cf-btn cf-btn-primary"
                    onClick={goToDashboard}
                  >
                    Dashboard
                  </button>
                  <button
                    type="button"
                    className="cf-nav-ghost"
                    onClick={goToLogout}
                    style={{ textAlign: "center", justifyContent: "center" }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="cf-nav-ghost"
                    onClick={goToSignin}
                    style={{ textAlign: "center", justifyContent: "center" }}
                  >
                    Intra in cont
                  </button>
                  <button
                    type="button"
                    className="cf-btn cf-btn-primary"
                    onClick={goToAuth}
                  >
                    Creeaza cont gratuit
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        <div className="cf-hero-body">
          <p className="cf-hero-label" aria-hidden="true">
            CADRUL DE SECURITATE CIBERNETICA
          </p>
          <h1 className="cf-hero-title">
            CyberFundamentals (CyFun®) — Cadrul Oficial DNSC pentru Securitate
            Cibernetica NIS2
          </h1>
          <p className="cf-hero-sub">
            Metodologia oficiala DNSC pentru evaluarea si implementarea
            masurilor de securitate cibernetica conform Directivei NIS2. 6
            functii, 22 categorii, 218 cerinte, 5 niveluri de maturitate —
            adoptat din Belgia (CCB), aliniat NIST CSF 2.0 si ISO 27001.
          </p>
        </div>
      </header>

      {/* ====== SECTION 1: FRAMEWORK INTRODUCTION ====== */}
      <section
        className="cf-section-light cf-section-reveal"
        ref={setSectionRef(0)}
        aria-labelledby="intro-heading"
      >
        <div className="cf-inner">
          <p className="cf-eyebrow">CADRUL DE SECURITATE CIBERNETICA</p>
          <h2 id="intro-heading" className="cf-h2" style={{ marginTop: 16 }}>
            CyberFundamentals (CyFun) — Cadrul Oficial DNSC
          </h2>
          <p className="cf-body" style={{ marginTop: 16 }}>
            CyFun este cadrul oficial adoptat de DNSC pentru evaluarea si
            implementarea masurilor de securitate cibernetica conform Directivei
            NIS2, transpusa prin OUG 155/2024 aprobata prin Legea 124/2025.
            Structurat in 6 functii de securitate, 22 categorii si 218 cerinte,
            CyFun nu este doar un instrument de evaluare — este o arhitectura
            completa de implementare a securitatii cibernetice.
          </p>
          <p className="cf-pill" style={{ marginTop: 20 }}>
            Dezvoltat de Centre for Cybersecurity Belgium (CCB), adaptat pentru
            Romania de DNSC
          </p>
          <div className="cf-intro-cards">
            <article className="cf-card">
              <span className="cf-card-tag">ORIGINEA — BELGIA</span>
              <h3 className="cf-card-title">Originea — Belgia</h3>
              <p className="cf-card-body">
                CyFun dezvoltat de CCB. Aliniat cu NIST CSF 2.0, ISO
                27001/27002, IEC 62443, CIS Controls. ENISA recomanda CyFun ca
                model referinta.
              </p>
              <div className="cf-card-tags">
                <span className="cf-card-pill">NIST CSF 2.0</span>
                <span className="cf-card-pill">ISO 27001</span>
                <span className="cf-card-pill">IEC 62443</span>
                <span className="cf-card-pill">CIS Controls</span>
              </div>
            </article>
            <article className="cf-card">
              <span className="cf-card-tag">ADOPTAREA — ROMANIA</span>
              <h3 className="cf-card-title">Adoptarea — Romania</h3>
              <p className="cf-card-body">
                DNSC a adoptat CyFun ca metodologie oficiala prin Proiectul
                Ordinului DNSC nr. 4/2025. Instrumente: EVAL_MMS_B/I/E pe
                dnsc.ro. Cadru legal: Art. 12(1),(4) OUG 155/2024 — autoevaluare
                anuala obligatorie.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ====== SECTION 2: THE 6 FUNCTIONS ====== */}
      <section
        className="cf-section-white cf-section-reveal"
        ref={setSectionRef(1)}
        aria-labelledby="functions-heading"
      >
        <div className="cf-inner" style={{ textAlign: "center" }}>
          <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
            CELE 6 FUNCTII
          </p>
          <h2
            id="functions-heading"
            className="cf-h2"
            style={{ marginTop: 16 }}
          >
            Arhitectura CyFun — Ciclul de Viata al Securitatii
          </h2>
          <p
            className="cf-body"
            style={{
              marginTop: 16,
              maxWidth: 800,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Fiecare functie depinde de functia anterioara — un lant in care cea
            mai slaba veriga determina rezilienta intregului sistem.
          </p>
          <div className="cf-fn-grid" style={{ textAlign: "left" }}>
            {functions.map((fn) => (
              <article
                key={fn.code}
                className="cf-fn-card"
                style={{ borderLeft: `3px solid ${fn.borderColor}` }}
              >
                <span className="cf-fn-code" style={{ color: fn.color }}>
                  {fn.code}
                </span>
                <h3 className="cf-fn-title">{fn.name}</h3>
                <span className="cf-fn-stats">{fn.reqs}</span>
                <p className="cf-fn-desc">{fn.desc}</p>
              </article>
            ))}
          </div>

          {/* Chain Visualization */}
          <div
            className="cf-chain"
            role="img"
            aria-label="Ciclul functiilor CyFun: GV, ID, PR, DE, RS, RC si inapoi la GV"
          >
            {functions.map((fn, i) => (
              <span key={fn.code} style={{ display: "contents" }}>
                <span
                  className="cf-chain-pill"
                  style={{ color: fn.color, background: `${fn.color}1A` }}
                >
                  {fn.code}
                </span>
                <span className="cf-chain-arrow" aria-hidden="true">
                  →
                </span>
                {i === functions.length - 1 && (
                  <span
                    className="cf-chain-pill"
                    style={{
                      color: functions[0].color,
                      background: `${functions[0].color}1A`,
                    }}
                  >
                    GV
                  </span>
                )}
              </span>
            ))}
          </div>
          <p className="cf-chain-note">
            RC nu este sfarsitul — este generatorul de lectii invatate care
            actualizeaza toate functiile anterioare, inchizand bucla nativa de
            imbunatatire continua.
          </p>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span className="cf-chain-summary">218 cerinte, 22 categorii</span>
          </div>
        </div>
      </section>

      {/* ====== SECTION 3: PRAGURI + PROCESUL ====== */}
      <section
        className="cf-section-light cf-section-reveal"
        ref={setSectionRef(2)}
        aria-labelledby="compliance-heading"
      >
        <div className="cf-inner">
          {/* Compliance Thresholds */}
          <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
            PRAGURI DE CONFORMITATE
          </p>
          <h2
            id="compliance-heading"
            className="cf-h3"
            style={{ marginTop: 16, lineHeight: 1.15 }}
          >
            Ce nivel de maturitate trebuie atins?
          </h2>
          <p className="cf-body" style={{ marginTop: 16 }}>
            Pragurile minime sunt diferentiate in functie de nivelul de risc
            (ENIRE@RO). Cerintele sunt cumulative.
          </p>
          <div className="cf-table-wrap">
            <table
              className="cf-table"
              aria-label="Praguri de conformitate CyFun pe nivel de risc"
            >
              <thead>
                <tr>
                  <th>Criteriu</th>
                  <th style={{ textAlign: "center" }}>
                    <span style={{ color: "#22C55E" }}>De Baza</span>
                  </th>
                  <th style={{ textAlign: "center" }}>
                    <span style={{ color: "#B45309" }}>Important</span>
                  </th>
                  <th style={{ textAlign: "center" }}>
                    <span style={{ color: "#F87171" }}>Esential</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Maturitate per masura-cheie (MC)</td>
                  <td style={{ textAlign: "center" }}>≥2.5</td>
                  <td style={{ textAlign: "center" }}>≥3.0</td>
                  <td style={{ textAlign: "center" }}>≥3.0</td>
                </tr>
                <tr>
                  <td>Maturitate per categorie</td>
                  <td style={{ textAlign: "center" }}>n/a</td>
                  <td style={{ textAlign: "center" }}>n/a</td>
                  <td style={{ textAlign: "center" }}>≥3.0</td>
                </tr>
                <tr>
                  <td>Maturitate totala (media generala)</td>
                  <td style={{ textAlign: "center" }}>≥2.5</td>
                  <td style={{ textAlign: "center" }}>≥3.0</td>
                  <td style={{ textAlign: "center" }}>≥3.5</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="cf-table-scroll-hint" aria-hidden="true">
            ← Gliseaza pentru a vedea toate coloanele →
          </p>
          <div className="cf-warning" role="alert">
            <TriangleAlert
              size={20}
              className="cf-warning-icon"
              aria-hidden="true"
            />
            <p className="cf-warning-text">
              Daca pragurile nu sunt atinse: Plan de remediere obligatoriu
              pentru entitatile esentiale (30 zile, art. 12(5) OUG 155/2024).
            </p>
          </div>

          {/* Evaluation Process */}
          <div style={{ marginTop: 64 }}>
            <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
              PROCESUL DE EVALUARE
            </p>
            <h2
              id="evaluation-heading"
              className="cf-h3"
              style={{ marginTop: 16 }}
            >
              Cum se realizeaza autoevaluarea maturitatii
            </h2>
            <p className="cf-body" style={{ marginTop: 16 }}>
              Autoevaluarea anuala este obligatorie conform art. 12(4) OUG
              155/2024. Instrumente oficiale: EVAL_MMS_B/I/E.
            </p>
            <div className="cf-steps">
              <article className="cf-step">
                <span className="cf-step-num">01</span>
                <h3 className="cf-step-title">
                  Determinare nivel risc (ENIRE@RO)
                </h3>
                <p className="cf-step-desc">
                  Nivelul determina instrumentul EVAL_MMS aplicabil entitatii.
                </p>
              </article>
              <article className="cf-step">
                <span className="cf-step-num">02</span>
                <h3 className="cf-step-title">Evaluare duala per cerinta</h3>
                <p className="cf-step-desc">
                  Documentatie (1-5) si implementare (1-5) — scor mediu per
                  cerinta.
                </p>
              </article>
              <article className="cf-step">
                <span className="cf-step-num">03</span>
                <h3 className="cf-step-title">
                  Verificare praguri si masuri-cheie
                </h3>
                <p className="cf-step-desc">
                  Scoruri minime MC, categorii, medie totala — conform tabelului
                  de praguri.
                </p>
              </article>
              <article className="cf-step">
                <span className="cf-step-num">04</span>
                <h3 className="cf-step-title">
                  Transmitere DNSC + Plan remediere
                </h3>
                <p className="cf-step-desc">
                  Fisier EVAL_MMS transmis oficial, asumat de management.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 4: CTA — CyFun Evaluation ====== */}
      <section
        className="cf-section-dark cf-section-reveal"
        ref={setSectionRef(3)}
        aria-labelledby="cta-heading"
      >
        <div className="cf-inner" style={{ textAlign: "center" }}>
          <p className="cf-hero-label">EVALUARE MATURITATE CYFUN</p>
          <h2
            id="cta-heading"
            className="cf-sora"
            style={{
              fontSize: "clamp(32px, 4vw, 42px)",
              fontWeight: 700,
              color: "#FFF",
              letterSpacing: -1.5,
              lineHeight: 1.08,
              maxWidth: 900,
              margin: "16px auto 0",
            }}
          >
            Pregatit sa evaluezi maturitatea CyFun?
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "#C7D2FE",
              lineHeight: 1.5,
              maxWidth: 800,
              margin: "16px auto 0",
            }}
          >
            NIS2PASS te ghideaza prin fiecare cerinta CyFun, pe cele 6 functii,
            cu explicatii contextuale si generare automata a fisierului EVAL_MMS
            conform formatelor cerute de DNSC.
          </p>
          <div className="cf-cta-stats">
            <div>
              <p className="cf-cta-stat-val" style={{ color: "#22C55E" }}>
                6
              </p>
              <p className="cf-cta-stat-label">functii securitate</p>
            </div>
            <div>
              <p className="cf-cta-stat-val" style={{ color: "#60A5FA" }}>
                218
              </p>
              <p className="cf-cta-stat-label">cerinte evaluate</p>
            </div>
            <div>
              <p className="cf-cta-stat-val" style={{ color: "#FBBF24" }}>
                5
              </p>
              <p className="cf-cta-stat-label">niveluri maturitate</p>
            </div>
            <div>
              <p className="cf-cta-stat-val" style={{ color: "#A78BFA" }}>
                22
              </p>
              <p className="cf-cta-stat-label">categorii</p>
            </div>
          </div>
          <div className="cf-cta-buttons">
            <button
              type="button"
              className="cf-btn cf-btn-primary"
              onClick={startEvaluation}
            >
              Incepe evaluarea CyFun — 100 credite gratuite{" "}
              <ArrowRight size={16} />
            </button>
            <button type="button" className="cf-btn cf-btn-ghost">
              Descarca EVAL_MMS de pe dnsc.ro
            </button>
          </div>
          <p className="cf-cta-note">
            Resurse oficiale: dnsc.ro, platformanis2.ro, EVAL_MMS_B/I/E
            disponibile pe site-ul DNSC
          </p>
        </div>
      </section>

      {/* ====== SECTION 5: FUNCTION DETAILS — GV, ID, PR ====== */}
      <section
        className="cf-section-light cf-section-reveal"
        ref={setSectionRef(4)}
        aria-labelledby="detail1-heading"
      >
        <div className="cf-inner">
          <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
            DETALIU FUNCTII
          </p>
          <h2 id="detail1-heading" className="cf-h3" style={{ marginTop: 16 }}>
            Functiile CyFun — Categorii si Cerinte-Cheie
          </h2>

          {/* GV Card */}
          <div className="cf-detail-card">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge"
                style={{
                  color: "#E8872A",
                  background: "rgba(232,135,42,0.12)",
                }}
              >
                GV
              </span>
              <span className="cf-detail-title">
                GUVERNARE — 40 cerinte, 12 MC
              </span>
            </div>
            <p className="cf-detail-desc">
              Stabileste tot ce este necesar inainte ca orice alta functie sa
              poata opera: context organizational, strategie de risc, roluri,
              politici, supraveghere si securitatea lantului de aprovizionare.
            </p>
            <div className="cf-subcats">
              {gvCategories.map((cat) => (
                <div key={cat.code} className="cf-subcat">
                  <span className="cf-subcat-code">{cat.code}</span>
                  <span className="cf-subcat-name">{cat.name}</span>
                  <span className="cf-subcat-count">{cat.count}</span>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* ID Card */}
          <div className="cf-detail-card">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge"
                style={{
                  color: "#60A5FA",
                  background: "rgba(96,165,250,0.12)",
                }}
              >
                ID
              </span>
              <span className="cf-detail-title">
                IDENTIFICARE — 55 cerinte, 6 MC
              </span>
            </div>
            <p className="cf-detail-desc">
              Ce avem, ce riscuri purtam, unde sunt punctele critice? Faza de
              descoperire si cartografiere.
            </p>
            <div className="cf-subcats-3">
              {idCategories.map((cat) => (
                <div key={cat.code} className="cf-subcat">
                  <span className="cf-subcat-code">{cat.code}</span>
                  <span className="cf-subcat-name">{cat.name}</span>
                  <span className="cf-subcat-count">{cat.count}</span>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* PR Card */}
          <div className="cf-detail-card">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge"
                style={{ color: "#22C55E", background: "rgba(34,197,94,0.12)" }}
              >
                PR
              </span>
              <span className="cf-detail-title">
                PROTECTIE — 79 cerinte, MC multiple
              </span>
            </div>
            <p className="cf-detail-desc">
              Cel mai mare volum de implementare — controale tehnice si
              organizationale.
            </p>
            <div className="cf-subcats">
              {prCategories.map((cat) => (
                <div key={cat.code} className="cf-subcat">
                  <span className="cf-subcat-code">{cat.code}</span>
                  <span className="cf-subcat-name">{cat.name}</span>
                  <span className="cf-subcat-count">{cat.count}</span>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 6: DE, RS, RC + MATURITY LEVELS ====== */}
      <section
        className="cf-section-white cf-section-reveal"
        ref={setSectionRef(5)}
        aria-labelledby="detail2-heading"
      >
        <div className="cf-inner">
          {/* DE, RS, RC */}
          <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
            DETALII FUNCTII
          </p>
          <h2 id="detail2-heading" className="cf-h3" style={{ marginTop: 16 }}>
            Detectare, Raspuns, Recuperare
          </h2>
          <p className="cf-body" style={{ marginTop: 16 }}>
            Ultimele trei functii CyFun completeaza ciclul de securitate — de la
            monitorizarea continua si detectarea amenintarilor, la gestionarea
            incidentelor si recuperarea operatiunilor.
          </p>

          {/* DE Card */}
          <div className="cf-detail-card-alt">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge-solid"
                style={{ background: "#A78BFA" }}
              >
                DE
              </span>
              <h3
                className="cf-sora"
                style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}
              >
                DETECTARE — 20 cerinte, 4 MC
              </h3>
            </div>
            <p className="cf-detail-desc">
              Monitorizare continua, jurnalizare, analiza evenimente adverse —
              capacitatea de a identifica amenintarile inainte de a deveni
              incidente.
            </p>
            <div className="cf-cat-list">
              {deCategories.map((cat) => (
                <div key={cat.code} className="cf-cat-item">
                  <div className="cf-cat-item-left">
                    <span className="cf-cat-item-code">{cat.code}</span>
                    <span className="cf-cat-item-name">{cat.name}</span>
                    {cat.desc && (
                      <span className="cf-cat-item-desc">{cat.desc}</span>
                    )}
                  </div>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* RS Card */}
          <div className="cf-detail-card-alt">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge-solid"
                style={{ background: "#F87171" }}
              >
                RS
              </span>
              <h3
                className="cf-sora"
                style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}
              >
                RASPUNS — 16 cerinte, 3 MC
              </h3>
            </div>
            <p className="cf-detail-desc">
              Gestionarea incidentelor, triaj, coordonare, raportare PNRISC —
              actiunea rapida dupa detectarea unei amenintari.
            </p>
            <div className="cf-cat-list">
              {rsCategories.map((cat) => (
                <div key={cat.code} className="cf-cat-item">
                  <div className="cf-cat-item-left">
                    <span className="cf-cat-item-code">{cat.code}</span>
                    <span className="cf-cat-item-name">{cat.name}</span>
                    {cat.desc && (
                      <span className="cf-cat-item-desc">{cat.desc}</span>
                    )}
                  </div>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* RC Card */}
          <div className="cf-detail-card-alt">
            <div className="cf-detail-header">
              <span
                className="cf-detail-badge-solid"
                style={{ background: "#FBBF24", color: "#0F172A" }}
              >
                RC
              </span>
              <h3
                className="cf-sora"
                style={{ fontSize: 20, fontWeight: 700, color: "#0F172A" }}
              >
                RECUPERARE — 8 cerinte, 1 MC
              </h3>
            </div>
            <p className="cf-detail-desc">
              Continuitate, redresare, lectii invatate → feedback la toate
              functiile. BCP per serviciu critic, DRP, teste DR trimestriale.
            </p>
            <div className="cf-cat-list">
              {rcCategories.map((cat) => (
                <div key={cat.code} className="cf-cat-item">
                  <div className="cf-cat-item-left">
                    <span className="cf-cat-item-code">{cat.code}</span>
                    <span className="cf-cat-item-name">{cat.name}</span>
                  </div>
                  {cat.mc && <span className="cf-mc-tag">{cat.mc}</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Maturity Levels */}
          <div style={{ marginTop: 64 }}>
            <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
              NIVELURI DE MATURITATE
            </p>
            <h2
              id="maturity-heading"
              className="cf-h3"
              style={{ marginTop: 16 }}
            >
              5 niveluri — evaluare pe doua dimensiuni
            </h2>
            <p className="cf-body" style={{ marginTop: 16 }}>
              Fiecare cerinta este evaluata pe doua axe: Documentare (politici,
              proceduri, planuri aprobate formal) si Implementare (dovezi de
              aplicare efectiva, metrici, audituri).
            </p>
            <div
              className="cf-levels"
              role="list"
              aria-label="Nivelurile de maturitate CyFun"
            >
              {maturityLevels.map((lvl) => (
                <div
                  key={lvl.level}
                  className={`cf-level${lvl.highlighted ? " cf-level-highlighted" : ""}`}
                  role="listitem"
                >
                  <span className="cf-level-num" style={{ color: lvl.color }}>
                    {lvl.level}
                  </span>
                  <span className="cf-level-name">{lvl.name}</span>
                  <p className="cf-level-desc">{lvl.desc}</p>
                </div>
              ))}
            </div>
            <div className="cf-callout">
              <p className="cf-callout-text">
                Cheia trecerii de la Nivel 3 la Nivel 4-5: KPI/KRI din GV.OV-03
                — definirea indicatorilor de performanta si risc care permit
                monitorizarea cantitativa a eficacitatii controalelor si
                demonstrarea imbunatatirii continue catre auditori si
                management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ====== SECTION 7: NIS2 MAPPING ====== */}
      <section
        className="cf-section-light cf-section-reveal"
        ref={setSectionRef(6)}
        aria-labelledby="mapping-heading"
      >
        <div className="cf-inner">
          <p className="cf-eyebrow" style={{ letterSpacing: "2px" }}>
            MAPARE NIS2 → CYFUN
          </p>
          <h2 id="mapping-heading" className="cf-h3" style={{ marginTop: 16 }}>
            Obligatiile art. 13 OUG 155/2024 mapate pe functiile CyFun
          </h2>
          <div className="cf-table-wrap">
            <table
              className="cf-table"
              aria-label="Maparea obligatiilor NIS2 pe functiile CyFun"
            >
              <thead>
                <tr>
                  <th style={{ width: 120 }}>Articol</th>
                  <th>Obligatie NIS2</th>
                  <th style={{ width: 200 }}>Functie CyFun</th>
                </tr>
              </thead>
              <tbody>
                {nis2Mapping.map((row) => (
                  <tr key={row.art}>
                    <td className="cf-table-art">{row.art}</td>
                    <td>{row.obligation}</td>
                    <td className="cf-table-cyfun">{row.cyfun}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cf-table-scroll-hint" aria-hidden="true">
            ← Gliseaza pentru a vedea toate coloanele →
          </p>
        </div>
      </section>

      {/* ====== FOOTER ====== */}
      <SharedFooter />

      {/* ====== BACK TO TOP ====== */}
      <button
        type="button"
        className={`cf-back-to-top${showBackToTop ? " visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Inapoi sus"
      >
        <ChevronUp size={20} aria-hidden="true" />
      </button>

      {/* ====== MOBILE STICKY CTA ====== */}
      <div className="cf-mobile-cta">
        <button
          type="button"
          className="cf-btn cf-btn-primary"
          onClick={startEvaluation}
        >
          Incepe evaluarea CyFun <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default CyFunFundamentals;
